import { useState, useEffect, useRef, useCallback } from 'react';
import { useTutorial } from '@/components/tutorial/use-tutorial';

export interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
  bottom: number;
  right: number;
}

const TOOLTIP_GAP = 12;
const TOOLTIP_MARGIN = 8;

const getTooltipWidth = () => Math.min(340, window.innerWidth - TOOLTIP_MARGIN * 2);

const TutorialTooltip = ({ targetRect }: { targetRect: TargetRect | null }) => {
  const { currentStep = 0, currentStepData, totalSteps = 0, waitingForInteraction, nextStep, prevStep, skipTutorial } =
    useTutorial();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [, setPlacement] = useState('bottom');
  const [tooltipWidth, setTooltipWidth] = useState(getTooltipWidth);

  const computePosition = useCallback(() => {
    if (!targetRect || !tooltipRef.current) return;

    const width = getTooltipWidth();
    setTooltipWidth(width);

    const tooltipHeight = tooltipRef.current.offsetHeight;
    const viewportHeight = window.innerHeight;

    const isMobile = window.innerWidth < 1024;
    const bottomNavHeight = isMobile ? 64 + parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sab') || '0', 10) : 0;
    const safeBottom = viewportHeight - bottomNavHeight;

    const spaceAbove = targetRect.top;
    const spaceBelow = safeBottom - targetRect.bottom;
    const preferred = currentStepData?.tooltipPosition || 'bottom';

    let finalPlacement = preferred;
    if (preferred === 'bottom' && spaceBelow < tooltipHeight + TOOLTIP_GAP && spaceAbove > tooltipHeight + TOOLTIP_GAP) {
      finalPlacement = 'top';
    } else if (preferred === 'top' && spaceAbove < tooltipHeight + TOOLTIP_GAP && spaceBelow > tooltipHeight + TOOLTIP_GAP) {
      finalPlacement = 'bottom';
    }

    let top: number;
    if (finalPlacement === 'bottom') {
      top = targetRect.bottom + TOOLTIP_GAP;
    } else {
      top = targetRect.top - tooltipHeight - TOOLTIP_GAP;
    }

    let left = targetRect.left + targetRect.width / 2 - width / 2;

    left = Math.max(TOOLTIP_MARGIN, Math.min(left, window.innerWidth - width - TOOLTIP_MARGIN));
    top = Math.max(TOOLTIP_MARGIN, Math.min(top, safeBottom - tooltipHeight - TOOLTIP_MARGIN));

    setPosition({ top, left });
    setPlacement(finalPlacement);
  }, [targetRect, currentStepData]);

  useEffect(() => {
    computePosition();
  }, [computePosition]);

  useEffect(() => {
    const handleResize = () => computePosition();
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, [computePosition]);

  if (!currentStepData) return null;

  const isInteractive = currentStepData.type === 'interactive' && waitingForInteraction;
  const isLast = currentStepData.isLast;
  const isFirst = currentStep === 0;

  return (
    <div
      ref={tooltipRef}
      className="fixed z-[61] pointer-events-auto"
      style={{
        top: position.top,
        left: position.left,
        width: tooltipWidth,
        animation: 'tutorial-tooltip-enter 0.3s ease-out',
      }}
    >
      <div className="bg-card rounded-2xl border-t-[5px] border-t-primary shadow-xl overflow-hidden">
        <div className="p-6">
          {/* Step badge */}
          <div className="text-[10px] font-bold tracking-[1.4px] uppercase text-primary mb-1.5">
            Étape {currentStep + 1} sur {totalSteps}
          </div>

          {/* Icon */}
          {currentStepData.icon && (
            <span className="text-2xl block mb-1.5">{currentStepData.icon}</span>
          )}

          {/* Title */}
          <h3 className="text-[15px] font-extrabold text-foreground mb-0.5">{currentStepData.title}</h3>

          {/* Subtitle */}
          {currentStepData.sub && (
            <p className="text-[12.5px] font-semibold text-primary italic mb-2">{currentStepData.sub}</p>
          )}

          {/* Description */}
          <p className="text-[13px] text-muted-foreground leading-relaxed mb-3.5">
            {currentStepData.description}
          </p>

          {/* Footer */}
          <div className="flex flex-col gap-2.5">
            {/* Dots */}
            <div className="flex gap-1.5">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`h-[7px] rounded-full transition-all duration-300 ${
                    i === currentStep
                      ? 'w-5 bg-primary'
                      : i < currentStep
                        ? 'w-[7px] bg-primary/40'
                        : 'w-[7px] bg-border'
                  }`}
                />
              ))}
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={skipTutorial}
                className="text-[12.5px] text-muted-foreground underline cursor-pointer bg-transparent border-none p-0 mr-auto"
              >
                Ignorer
              </button>

              {isInteractive ? (
                <span className="text-xs text-primary font-medium animate-pulse text-right">
                  Cliquez sur l'élément
                </span>
              ) : (
                <>
                  <button
                    onClick={prevStep}
                    disabled={isFirst}
                    className="bg-muted text-muted-foreground border-none rounded-lg px-4 py-2 text-[13px] font-semibold cursor-pointer disabled:opacity-30"
                  >
                    {'\u2190'} Préc.
                  </button>
                  <button
                    onClick={nextStep}
                    className="bg-primary text-white border-none rounded-lg px-3.5 py-2 text-[13px] font-semibold cursor-pointer"
                  >
                    {isLast ? 'Terminer \u2713' : 'Suivant \u2192'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialTooltip;
